import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  FormControlLabel,
  MenuItem,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useState, useEffect } from "react";
import { CurrencyField } from "./CurrencyField";

export default function AcrInputs({
  additionalCapitalRepayments,
  dealLength,
  maxAffordable,
  savingsInterestRate,
  saveAtStart,
  additionalCapitalRepaymentPercentageLimit,
  reduceTermOnAdditionalCapitalRepayment,
  skipAdditionalCapitalRepayments,
  interestOnly,
  onChange,
  alwaysExpanded = false,
  hideMonthlyBudget = false,
  hideSkipAcr = false,
}: {
  additionalCapitalRepayments: boolean | undefined;
  dealLength: number | undefined;
  maxAffordable: number | undefined;
  savingsInterestRate: number | undefined;
  saveAtStart: boolean | undefined;
  additionalCapitalRepaymentPercentageLimit: number | undefined;
  reduceTermOnAdditionalCapitalRepayment: boolean | undefined;
  skipAdditionalCapitalRepayments: boolean | undefined;
  interestOnly: boolean | undefined;
  onChange: (changes: {
    additionalCapitalRepayments?: boolean;
    dealLength?: number | undefined;
    maxAffordable?: number | undefined;
    savingsInterestRate?: number | undefined;
    saveAtStart?: boolean | undefined;
    additionalCapitalRepaymentPercentageLimit?: number | undefined;
    reduceTermOnAdditionalCapitalRepayment?: boolean;
    skipAdditionalCapitalRepayments?: boolean;
  }) => void;
  alwaysExpanded?: boolean;
  hideMonthlyBudget?: boolean;
  hideSkipAcr?: boolean;
}) {
  const [includeSavingsInterest, setIncludeSavingsInterest] = useState<boolean>(
    savingsInterestRate !== undefined && savingsInterestRate > 0,
  );

  // Sync the toggle state when savingsInterestRate prop changes (e.g., from demo button)
  useEffect(() => {
    setIncludeSavingsInterest(
      savingsInterestRate !== undefined && savingsInterestRate > 0,
    );
  }, [savingsInterestRate]);

  // Determine if content should be shown
  const showContent = alwaysExpanded || additionalCapitalRepayments;

  return (
    <Box sx={{ mt: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="h6">Additional Capital Repayments</Typography>
        {!alwaysExpanded && (
          <ToggleButtonGroup
            value={additionalCapitalRepayments}
            exclusive
            onChange={(_, value) => {
              if (value !== null) {
                onChange({ additionalCapitalRepayments: value });
              }
            }}
            size="small"
          >
            <ToggleButton
              value={true}
              sx={{
                "&.Mui-selected": {
                  bgcolor: "success.main",
                  color: "success.contrastText",
                  "&:hover": {
                    bgcolor: "success.dark",
                  },
                },
              }}
            >
              ON
            </ToggleButton>
            <ToggleButton value={false}>OFF</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {!showContent && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Additional Capital Repayments (ACR) allow more than the required
          monthly payment to be paid towards the mortgage, reducing the
          principal faster and saving on interest.
        </Typography>
      )}

      {showContent && (
        <>
          <Typography variant="body2" color="text.secondary" paragraph>
            Each month the <em>Monthly Budget</em> is deposited into an
            Additional Capital Repayment (ACR) pot. The regular monthly mortgage
            payment is also taken from that pot. The pot can also be configured
            to earn interest.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            During a fixed deal term a maximum of one ACR per year is made,
            capped at 10% of the outstanding balance at payment time, unless
            changed in advanced settings. These are spaced evenly through the
            recurring <em>Fixed Deal Term</em> (e.g. 2 years: months 8 and 16)
            with an additional ACR at the end (e.g. month 24 between deals).
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            paragraph
            sx={{ mb: 2 }}
          >
            If the pot exceeds the remaining annual allowance early, that year's
            ACR is triggered immediately. Between fixed deal terms ACRs are
            assumed unlimited.
          </Typography>
          {!hideMonthlyBudget && (
            <CurrencyField
              label="Monthly Budget"
              value={maxAffordable}
              onChange={(val) => onChange({ maxAffordable: val })}
              fullWidth
              margin="normal"
              required
            />
          )}
          <TextField
            select
            label="Fixed Deal Term"
            value={dealLength ?? ""}
            onChange={(e) =>
              onChange({
                dealLength:
                  e.target.value === ""
                    ? undefined
                    : parseInt(e.target.value, 10),
              })
            }
            fullWidth
            margin="normal"
            required
          >
            <MenuItem value={12}>1 year</MenuItem>
            <MenuItem value={24}>2 years</MenuItem>
            <MenuItem value={36}>3 years</MenuItem>
            <MenuItem value={48}>4 years</MenuItem>
            <MenuItem value={60}>5 years</MenuItem>
          </TextField>

          <Box
            sx={{
              p: 2,
              mt: 2,
              mb: 3,
              borderRadius: 1,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: "rgba(0, 0, 0, 0.23)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
              onClick={() => {
                setIncludeSavingsInterest(!includeSavingsInterest);
                if (includeSavingsInterest) {
                  onChange({ savingsInterestRate: undefined });
                }
              }}
            >
              <Typography component="span">
                Earn interest on leftover budget
              </Typography>
              <ToggleButtonGroup
                value={includeSavingsInterest}
                exclusive
                onChange={(_, value) => {
                  if (value !== null) {
                    setIncludeSavingsInterest(value);
                    if (!value) {
                      onChange({ savingsInterestRate: undefined });
                    }
                  }
                }}
                size="small"
                sx={{ height: "1.6rem" }}
              >
                <ToggleButton
                  value={true}
                  sx={{
                    "&.Mui-selected": {
                      bgcolor: "success.main",
                      color: "success.contrastText",
                      "&:hover": {
                        bgcolor: "success.dark",
                      },
                    },
                  }}
                >
                  ON
                </ToggleButton>
                <ToggleButton value={false}>OFF</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {includeSavingsInterest && (
              <TextField
                label="Interest Rate (%)"
                type="number"
                slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
                defaultValue={savingsInterestRate ?? ""}
                key={savingsInterestRate} // Reset when prop changes (e.g., demo button)
                onBlur={(e) =>
                  onChange({
                    savingsInterestRate:
                      e.target.value === ""
                        ? undefined
                        : parseFloat(e.target.value),
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    const target = e.target as HTMLInputElement;
                    onChange({
                      savingsInterestRate:
                        target.value === ""
                          ? undefined
                          : parseFloat(target.value),
                    });
                    target.blur(); // Remove focus to prevent form submission
                  }
                }}
                fullWidth
                margin="normal"
                sx={{ mb: 0 }}
              />
            )}
          </Box>
          <div>
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="advanced-content"
                id="advanced-header"
              >
                <Typography component="span">Advanced Settings</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField
                  label="Annual Overpayment Allowance (% of Loan)"
                  type="number"
                  inputProps={{ step: 0.01 }}
                  value={additionalCapitalRepaymentPercentageLimit ?? ""}
                  onChange={(e) =>
                    onChange({
                      additionalCapitalRepaymentPercentageLimit:
                        e.target.value === ""
                          ? undefined
                          : parseFloat(e.target.value),
                    })
                  }
                  fullWidth
                  margin="normal"
                  required
                  sx={{ mt: 0, mb: 1 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, mb: 2, px: 1 }}
                >
                  Many lenders allow up to a percentage of the loan to be
                  overpaid each year without penalty.
                </Typography>
                <Divider />
                <FormControlLabel
                  control={
                    <Switch
                      checked={reduceTermOnAdditionalCapitalRepayment}
                      onChange={(e) =>
                        onChange({
                          reduceTermOnAdditionalCapitalRepayment:
                            e.target.checked,
                        })
                      }
                      disabled={interestOnly}
                    />
                  }
                  label="Payments reduce mortgage term"
                  sx={{ mt: 2, px: 1 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, mb: 2, pl: 1 }}
                >
                  By default, additional capital repayments reduce the monthly
                  payment while keeping the mortgage term the same. Enabling
                  this option will instead keep the monthly payment the same and
                  shorten the mortgage term. This option is disabled for
                  interest only mortgages.
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, mb: 2, pl: 1 }}
                >
                  <strong>Note:</strong> when this option is disabled, the term
                  will still be reduced between mortgage deals to align the
                  monthly payment to the value chosen above.
                </Typography>
                <Divider />
                <FormControlLabel
                  disabled={!includeSavingsInterest}
                  control={
                    <Switch
                      checked={saveAtStart ?? false}
                      onChange={(e) => {
                        onChange({ saveAtStart: e.target.checked });
                      }}
                    />
                  }
                  label="Deposit budget at start of month"
                  sx={{ mt: 2, pl: 1 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, mb: 1, pl: 1 }}
                >
                  By default, the monthly budget is deposited at the end of the
                  month after the mortgage payment is taken. Enabling this
                  option assumes the budget is deposited at the start of the
                  month, allowing interest to be earned on it before the
                  mortgage payment is made. This setting is only relevant if
                  savings interest is enabled above.
                </Typography>
                {!hideSkipAcr && (
                  <>
                    <Divider />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={skipAdditionalCapitalRepayments ?? false}
                          onChange={(e) => {
                            onChange({
                              skipAdditionalCapitalRepayments: e.target.checked,
                            });
                          }}
                        />
                      }
                      label="Skip additional capital repayments"
                      sx={{ mt: 2, pl: 1 }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, mb: 1, pl: 1 }}
                    >
                      By default, additional capital repayments are made to the
                      mortgage when conditions are met. Enabling this option
                      will skip making any additional capital repayments and
                      instead keep the money in the reserve account. This is
                      useful for creating simulations to compare savings versus
                      making overpayments.
                    </Typography>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          </div>
        </>
      )}
    </Box>
  );
}
